import type { ReadonlyDeep } from "type-fest";
import * as Sentry from "@sentry/react";
import { clsx } from "clsx";
import type { ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

import type { Href } from "@/types";

export const cn = (...inputs: ReadonlyDeep<ClassValue[]>) => twMerge(clsx(inputs));

export const resolveUrl = (url: ReadonlyDeep<Href>): string =>
  typeof url === "string" ? url : url.url;

export const isSameUrl = (url1: ReadonlyDeep<Href>, url2: ReadonlyDeep<Href>) =>
  resolveUrl(url1) === resolveUrl(url2);

export const literal = <const T>(value: T): T => value;

/**
 * Detaches a promise from the call site while still surfacing rejections to
 * Sentry. Use when the caller has already arranged a separate UX feedback path
 * (toast, form error callback, internal try/catch) and only needs to drop the
 * await without losing telemetry on bugs.
 *
 * Never use this to bypass error handling. If the call site cannot handle
 * failure elsewhere, do not hide it here.
 */
export const fireAndForget = <T>(promise: Readonly<Promise<T>>): void => {
  void (async () => {
    try {
      await promise;
    } catch (error) {
      Sentry.captureException(error, { tags: { boundary: "fire-and-forget" } });
    }
  })();
};
